import React, { useEffect, useRef } from 'react';
import { verifyPayment } from '../../services/api/payments';
import useAuthStore from '../../store/authStore';

interface PayPalButtonProps {
  amount: number;
  onSuccess: (details: any) => void;
  onError?: (error: any) => void;
  onInvalidAmount?: () => void;
}

declare global {
  interface Window {
    paypal?: any;
    VITE_PAYPAL_CLIENT_ID?: string;
  }
}

const PayPalButton: React.FC<PayPalButtonProps> = ({ 
  amount, 
  onSuccess, 
  onError,
  onInvalidAmount 
}) => {
  const paypalButtonRef = useRef<HTMLDivElement>(null);
  const { login } = useAuthStore();

  useEffect(() => {
    if (!window.paypal || !paypalButtonRef.current) return;

    // Validate minimum amount
    if (amount < 2) {
      onInvalidAmount?.();
      return;
    }

    // Clear any existing buttons
    paypalButtonRef.current.innerHTML = '';

    // Calculate final amount with volume discounts
    const calculateFinalAmount = (baseAmount: number) => {
      if (baseAmount >= 500) {
        return baseAmount * 0.90; // 10% discount
      } else if (baseAmount >= 100) {
        return baseAmount * 0.95; // 5% discount
      }
      return baseAmount;
    };

    const finalAmount = calculateFinalAmount(amount);

    window.paypal.Buttons({
      style: {
        layout: 'vertical',
        color: 'blue',
        shape: 'rect',
        label: 'pay'
      },
      createOrder: (_: any, actions: any) => {
        return actions.order.create({
          intent: 'CAPTURE',
          purchase_units: [{
            amount: {
              value: finalAmount.toFixed(2),
              currency_code: 'USD'
            },
            description: `Prestige AI Credits - $${amount.toFixed(2)}${
              finalAmount < amount ? ` (Discounted from $${amount.toFixed(2)})` : ''
            }`
          }]
        });
      },
      onApprove: async (data: any, actions: any) => {
        try {
          const details = await actions.order.capture();
          console.log('Payment captured:', details);
          
          const response = await verifyPayment(data.orderID);
          
          if (!response.success) {
            throw new Error('Payment verification failed');
          }

          // Update local auth state with new credits
          const user = useAuthStore.getState().user;
          if (user) {
            login(localStorage.getItem('auth-token') || '', {
              ...user,
              credits: response.credits
            });
          }

          onSuccess(response);
        } catch (error) {
          console.error('Payment processing error:', error);
          onError?.(error);
        }
      },
      onError: (err: any) => {
        console.error('PayPal Error:', err);
        onError?.(err);
      }
    }).render(paypalButtonRef.current);
  }, [amount, onSuccess, onError, onInvalidAmount, login]);

  return (
    <div className="p-4 bg-white rounded-lg shadow">
      <div className="text-center">
        <p className="text-lg font-semibold mb-4">
          Add Funds: ${amount.toFixed(2)}
          {amount >= 500 && (
            <span className="block text-sm text-green-600">10% volume discount applied!</span>
          )}
          {amount >= 100 && amount < 500 && (
            <span className="block text-sm text-green-600">5% volume discount applied!</span>
          )}
        </p>
        <div ref={paypalButtonRef} />
      </div>
    </div>
  );
};

export default PayPalButton;